"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHrOptionsState = exports.HrOptionsStateProvider = exports.HrOptionsState = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const ui_1 = require("@jsonjoy.com/ui");
const slate_1 = require("slate");
const slate_react_1 = require("slate-react");
const settings = tslib_1.__importStar(require("./settings"));
const getElementSignature = (element) => JSON.stringify([
    element.strokeWidth ?? null,
    element.lineWidth ?? null,
    element.lineStyle ?? null,
    element.blockHeight ?? null,
    element.caption ?? '',
]);
const HrOptionsStateContext = React.createContext(null);
class HrOptionsState {
    constructor(editor, element, closePopup) {
        this.editor = editor;
        this.strokeWidth = ui_1.rsync.val(settings.DEFAULT_HR_STROKE_WIDTH);
        this.lineWidth = ui_1.rsync.val(settings.DEFAULT_HR_LINE_WIDTH);
        this.lineStyle = ui_1.rsync.val(settings.DEFAULT_HR_LINE_STYLE);
        this.blockHeight = ui_1.rsync.val(settings.DEFAULT_HR_BLOCK_HEIGHT);
        this.caption = ui_1.rsync.val('');
        this.signature = '';
        this.setElement = (element) => {
            const signature = getElementSignature(element);
            this.element = element;
            if (signature === this.signature)
                return;
            this.syncFromElement(element, signature);
        };
        this.setClosePopup = (closePopup) => {
            this.closePopup = closePopup;
        };
        this.setStrokeWidth = (value) => {
            this.strokeWidth.set(settings.getHrStrokeWidth(value));
        };
        this.setLineWidth = (value) => {
            this.lineWidth.set(settings.getHrLineWidth(value));
        };
        this.setLineStyle = (value) => {
            this.lineStyle.set(settings.getHrLineStyle(value));
        };
        this.setBlockHeight = (value) => {
            this.blockHeight.set(settings.getHrBlockHeight(value));
        };
        this.setCaption = (value) => {
            this.caption.set(value);
        };
        this.cancel = () => {
            this.syncFromElement(this.element);
            this.closePopup?.();
        };
        this.apply = () => {
            const nextStrokeWidth = settings.getStoredHrStrokeWidth(this.strokeWidth.value);
            const nextLineWidth = settings.getStoredHrLineWidth(this.lineWidth.value);
            const nextLineStyle = settings.getStoredHrLineStyle(this.lineStyle.value);
            const nextBlockHeight = settings.getStoredHrBlockHeight(this.blockHeight.value);
            const nextCaption = this.caption.value.trim();
            this.applyNumber('strokeWidth', nextStrokeWidth, this.element.strokeWidth);
            this.applyNumber('lineWidth', nextLineWidth, this.element.lineWidth);
            this.applyEnum('lineStyle', nextLineStyle, this.element.lineStyle);
            this.applyNumber('blockHeight', nextBlockHeight, this.element.blockHeight);
            this.applyString('caption', nextCaption, this.element.caption);
            this.caption.set(nextCaption);
            this.closePopup?.();
        };
        this.dispose = () => { };
        this.syncFromElement = (element, signature = getElementSignature(element)) => {
            this.signature = signature;
            this.strokeWidth.set(settings.getHrStrokeWidth(element.strokeWidth));
            this.lineWidth.set(settings.getHrLineWidth(element.lineWidth));
            this.lineStyle.set(settings.getHrLineStyle(element.lineStyle));
            this.blockHeight.set(settings.getHrBlockHeight(element.blockHeight));
            this.caption.set(element.caption ?? '');
        };
        this.applyNumber = (field, value, currentValue) => {
            if (value === currentValue)
                return;
            const path = slate_react_1.ReactEditor.findPath(this.editor, this.element);
            if (value !== undefined) {
                slate_1.Transforms.setNodes(this.editor, { [field]: value }, { at: path });
            }
            else {
                slate_1.Transforms.unsetNodes(this.editor, field, { at: path });
            }
        };
        this.applyEnum = (field, value, currentValue) => {
            if (value === currentValue)
                return;
            const path = slate_react_1.ReactEditor.findPath(this.editor, this.element);
            if (value !== undefined) {
                slate_1.Transforms.setNodes(this.editor, { [field]: value }, { at: path });
            }
            else {
                slate_1.Transforms.unsetNodes(this.editor, field, { at: path });
            }
        };
        this.applyString = (field, value, currentValue) => {
            const prev = (currentValue ?? '').trim();
            if (value === prev)
                return;
            const path = slate_react_1.ReactEditor.findPath(this.editor, this.element);
            if (value) {
                slate_1.Transforms.setNodes(this.editor, { [field]: value }, { at: path });
            }
            else {
                slate_1.Transforms.unsetNodes(this.editor, field, { at: path });
            }
        };
        this.element = element;
        this.closePopup = closePopup;
        this.syncFromElement(element);
    }
}
exports.HrOptionsState = HrOptionsState;
const HrOptionsStateProvider = ({ editor, element, closePopup, children, }) => {
    const stateRef = React.useRef(null);
    if (!stateRef.current)
        stateRef.current = new HrOptionsState(editor, element, closePopup);
    const state = stateRef.current;
    React.useEffect(() => {
        state.setClosePopup(closePopup);
    }, [closePopup, state]);
    React.useEffect(() => {
        state.setElement(element);
    }, [element, state]);
    React.useEffect(() => {
        return () => state.dispose();
    }, [state]);
    return React.createElement(HrOptionsStateContext.Provider, { value: state }, children);
};
exports.HrOptionsStateProvider = HrOptionsStateProvider;
const useHrOptionsState = () => {
    const state = React.useContext(HrOptionsStateContext);
    if (!state)
        throw new Error('HrOptionsStateContext is not available.');
    return state;
};
exports.useHrOptionsState = useHrOptionsState;
